import { useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';

import { PageContainer } from '~/components/layouts/page-container';
import { Badge } from '~/components/ui/badge';
import { Separator } from '~/components/ui/separator';
import { useDocumentTitle } from '~/hooks/use-document-title';
import { meQueryOptions } from '~/lib/auth';
import { departmentsQueryOptions } from '~/lib/departments';

export const Route = createFileRoute('/_authenticated/settings/')({
  component: SettingsPage
});

function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return (parts[0] ?? '').slice(0, 2).toUpperCase();
  const first = parts[0]?.[0] ?? '';
  const last = parts[parts.length - 1]?.[0] ?? '';
  return (first + last).toUpperCase();
}

function SettingsPage(): React.ReactNode {
  const { t } = useTranslation();
  useDocumentTitle(t('nav.settings'));
  const { data: session } = useQuery(meQueryOptions);
  const { data: deptData } = useQuery(departmentsQueryOptions);

  const myDepartments = (deptData?.departments ?? []).filter((d) =>
    session?.departmentIds.includes(d.id)
  );

  return (
    <PageContainer breadcrumbs={[{ label: t('nav.settings') }]} title={t('nav.settings')}>
      <div className="rounded-lg border border-border bg-card shadow-sm">
        <div className="flex items-center gap-5 p-6">
          <div className="flex size-16 shrink-0 items-center justify-center rounded-full bg-primary text-lg font-medium text-primary-foreground">
            {session?.name ? getInitials(session.name) : '—'}
          </div>
          <div className="flex min-w-0 flex-col gap-1.5">
            <p className="text-base font-semibold leading-none">{session?.name}</p>
            <p className="text-sm text-muted-foreground">{session?.email}</p>
            {session?.role && (
              <Badge variant="secondary" className="mt-0.5 w-fit">
                {t(`team.roles.${session.role}`)}
              </Badge>
            )}
          </div>
        </div>

        {myDepartments.length > 0 && (
          <>
            <Separator />
            <div className="flex flex-col gap-3 p-6">
              <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                {t('settings.profile.departments')}
              </p>
              <div className="flex flex-wrap gap-2">
                {myDepartments.map((dept) => (
                  <Badge key={dept.id} variant="outline">
                    {dept.name}
                  </Badge>
                ))}
              </div>
            </div>
          </>
        )}
      </div>
    </PageContainer>
  );
}
